import { User, UserRole } from '@/types/user.types';

/**
 * Mock Authentication API
 *
 * This file provides mock authentication responses for development when the backend server is unavailable.
 * To enable: Set USE_MOCK_AUTH = true
 * To disable: Set USE_MOCK_AUTH = false (connects to real backend)
 */

export const USE_MOCK_AUTH = false; // Toggle this to enable/disable mock auth

// Mock user data for different roles
export const MOCK_USERS: Record<string, User> = {
  customer: {
    id: '1',
    email: 'customer@test.com',
    firstName: 'John',
    lastName: 'Customer',
    role: UserRole.CUSTOMER,
    phoneNumber: '+1234567890',
    isActive: true,
    isEmailVerified: true,
    isVerified: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  farmer: {
    id: '2',
    email: 'farmer@test.com',
    firstName: 'Jane',
    lastName: 'Farmer',
    role: UserRole.FARMER,
    phoneNumber: '+1234567891',
    isActive: true,
    isEmailVerified: true,
    isVerified: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  delivery_agent: {
    id: '3',
    email: 'delivery@test.com',
    firstName: 'Mike',
    lastName: 'Delivery',
    role: UserRole.DELIVERY_AGENT,
    phoneNumber: '+1234567892',
    isActive: true,
    isEmailVerified: true,
    isVerified: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  supplier: {
    id: '4',
    email: 'supplier@test.com',
    firstName: 'Sarah',
    lastName: 'Supplier',
    role: UserRole.SUPPLIER,
    phoneNumber: '+1234567893',
    isActive: true,
    isEmailVerified: true,
    isVerified: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  admin: {
    id: '5',
    email: 'admin@test.com',
    firstName: 'Admin',
    lastName: 'User',
    role: UserRole.ADMIN,
    phoneNumber: '+1234567894',
    isActive: true,
    isEmailVerified: true,
    isVerified: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
};

// Mock tokens
export const MOCK_TOKENS = {
  access_token: 'mock-access-token-' + Date.now(),
  refresh_token: 'mock-refresh-token-' + Date.now(),
  token_type: 'bearer',
};

/**
 * Mock login function
 * Returns user based on email domain or defaults to customer
 */
export const mockLogin = async (email: string, _password: string) => {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 500));

  // Determine user role based on email
  let user: User;
  if (email.includes('farmer')) {
    user = MOCK_USERS.farmer;
  } else if (email.includes('delivery')) {
    user = MOCK_USERS.delivery_agent;
  } else if (email.includes('supplier')) {
    user = MOCK_USERS.supplier;
  } else if (email.includes('admin')) {
    user = MOCK_USERS.admin;
  } else {
    user = MOCK_USERS.customer;
  }

  // Override email with the one provided
  user = { ...user, email };

  return {
    user,
    accessToken: MOCK_TOKENS.access_token,
    refreshToken: MOCK_TOKENS.refresh_token,
  };
};

/**
 * Mock register function
 * Creates a new user based on the provided role
 */
export const mockRegister = async (data: {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  role: 'customer' | 'farmer' | 'delivery_agent' | 'supplier';
}) => {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 800));

  const newUser: User = {
    id: String(Math.floor(Math.random() * 1000) + 10),
    email: data.email,
    firstName: data.first_name,
    lastName: data.last_name,
    role:
      data.role === 'customer'
        ? UserRole.CUSTOMER
        : data.role === 'farmer'
          ? UserRole.FARMER
          : data.role === 'delivery_agent'
            ? UserRole.DELIVERY_AGENT
            : UserRole.SUPPLIER,
    phoneNumber: data.phone_number || '',
    isActive: true,
    isEmailVerified: false, // New users are not verified
    isVerified: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  return {
    user: newUser,
    message: 'Registration successful! Please verify your email.',
  };
};

/**
 * Mock get current user function
 */
export const mockGetCurrentUser = async () => {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 300));

  // Return customer by default (you can change this)
  return MOCK_USERS.customer;
};

/**
 * Mock logout function
 */
export const mockLogout = async () => {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 200));

  return { message: 'Logout successful' };
};
